import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { logout } from '../../store/actions/user.actions';
import { QRCodeModal } from '../profile/QRCodeModal';
import { ProfileModal } from '../profile/ProfileModal';
import { InstallButton } from '../pwa/InstallButton';
import authService from '../../services/auth';
import { User } from '../../types';
import { useBranding } from '../../hooks/useBranding';
import { NotificationService } from '../../utils/notifications';
import { getAvatarColors } from '../../utils/avatarColors';
// import { getNewMemberCount } from '../../api/userApi'; // Backend API not yet implemented

export const AppBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loginUser: user } = useAppSelector((state) => state.user);
  const { conversations } = useAppSelector((state) => state.messages);
  const [menuOpen, setMenuOpen] = useState(false); // Avatar menu (profile/settings)
  const [navMenuOpen, setNavMenuOpen] = useState(false); // Mobile nav menu
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [detailedUser, setDetailedUser] = useState<User | null>(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => NotificationService.isEnabled());
  const notificationsBlocked = 'Notification' in window && Notification.permission === 'denied';

  useEffect(() => {
    setNotificationsEnabled(NotificationService.isEnabled());
  }, [menuOpen]);

  const handleToggleNotifications = async () => {
    if (notificationsBlocked) {
      alert('Notifications are blocked by your browser. To enable them, open your browser site settings and allow notifications for this site.');
      return;
    }
    if (notificationsEnabled) {
      await NotificationService.disable();
      setNotificationsEnabled(false);
    } else {
      const granted = await NotificationService.enable();
      setNotificationsEnabled(granted);
    }
  };

  const branding = useBranding();

  const userName = user?.displayName || user?.display_name || user?.firstName || user?.first_name || user?.email || '?';
  const userInitial = userName.charAt(0).toUpperCase();
  const avatarColors = getAvatarColors(userName, user?.avatar_color);

  // Check if user is admin (ID 1) or manager (ID 2)
  const userId = String(user?.id || '');
  const isAdminOrManager = userId === '1' || userId === '2';

  // Calculate total unread messages
  const totalUnreadMessages = conversations.reduce((total, conv) => total + conv.unreadCount, 0);

  // Notification bell state
  const [bellOpen, setBellOpen] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [notifications, setNotifications] = useState<{ id: string; type: string; title: string; body: string; action?: string }[]>([]);
  const [loginAlerts, setLoginAlerts] = useState<{ id: string; type: string; title: string; body: string; action?: string }[]>([]);
  const bellRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const buildNotifications = async () => {
      const items: typeof notifications = [];

      if (totalUnreadMessages > 0) {
        items.push({
          id: 'messages',
          type: 'message',
          title: `${totalUnreadMessages} unread message${totalUnreadMessages > 1 ? 's' : ''}`,
          body: 'Tap to open your conversations',
          action: '/dashboard/messages',
        });
      }

      if (isAdminOrManager) {
        try {
          const res = await fetch('/api/auth/admin/pending-approvals', { credentials: 'include' });
          const json = await res.json();
          const count = json?.data?.users?.length || 0;
          setPendingApprovals(count);
          if (count > 0) {
            items.push({
              id: 'approvals',
              type: 'approval',
              title: `${count} pending member approval${count > 1 ? 's' : ''}`,
              body: 'New users are waiting for your approval',
              action: '/dashboard/settings',
            });
          }
        } catch {}
      }

      setNotifications(items);
    };

    buildNotifications();
    const t = setInterval(buildNotifications, 60_000);
    return () => clearInterval(t);
  }, [totalUnreadMessages, isAdminOrManager]);

  // Real-time login alerts for admin — socket pushes on every user login
  useEffect(() => {
    if (!isAdminOrManager) return;
    const { getActiveMessagingInstance } = require('../../services/messaging/providers/ServerMessagingProvider');
    const tryBind = () => {
      const socket = getActiveMessagingInstance()?.getSocket();
      if (!socket) return false;
      const onLogin = (data: any) => {
        const loc = [data.city, data.country].filter(Boolean).join(', ') || 'Unknown location';
        const ua  = (data.userAgent || '').match(/(iPhone|iPad|Android|Windows|Mac|Linux)/i)?.[1] || 'Unknown device';
        setLoginAlerts(prev => [{
          id: `login-${data.ts}`,
          type: 'login',
          title: `${data.name} just logged in`,
          body: `${loc} · ${ua} · ${data.ip}`,
          action: '/dashboard/settings',
        }, ...prev].slice(0, 10)); // keep last 10
      };
      socket.on('login-notification', onLogin);
      return () => socket.off('login-notification', onLogin);
    };
    // Poll until socket is ready
    let cleanup: (() => void) | false = false;
    const interval = setInterval(() => {
      const result = tryBind();
      if (result) { cleanup = result; clearInterval(interval); }
    }, 500);
    cleanup = tryBind();
    return () => { clearInterval(interval); if (cleanup) cleanup(); };
  }, [isAdminOrManager]);

  const totalNotifications = notifications.length + loginAlerts.length;

  useEffect(() => {
    if (!bellOpen) return;
    const handler = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) setBellOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [bellOpen]);

  // Load new member count for admin/manager - Backend API pending
  /*
  useEffect(() => {
    const loadNewMemberCount = async () => {
      if (!isAdminOrManager || !user) return;

      try {
        const response = await getNewMemberCount();
        if (response.data?.success && response.data.data) {
          setNewMemberCount(response.data.data.count || 0);
        }
      } catch (error) {
        console.error('Failed to load new member count:', error);
      }
    };

    loadNewMemberCount();

    // Refresh count every 5 minutes
    const interval = setInterval(loadNewMemberCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAdminOrManager, user]);
  */

  const handleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleNavMenu = () => {
    setNavMenuOpen(!navMenuOpen);
  };

  const handleClose = () => {
    setMenuOpen(false);
  };

  const handleNavClose = () => {
    setNavMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      setMenuOpen(false);
      await dispatch(logout());
      // Redirect to auth page after successful logout
      navigate('/auth');
    } catch (error) {
      console.error('Logout failed:', error);
      setMenuOpen(false);
    }
  };

  const handleNavigateToDirectory = () => {
    handleNavClose();
    navigate('/dashboard/directory');
  };

  const handleNavigateToMap = () => {
    handleNavClose();
    navigate('/dashboard/map');
  };

  const handleNavigateToCalendar = () => {
    handleNavClose();
    navigate('/dashboard/calendar');
  };

  const handleNavigateToHome = () => {
    handleNavClose();
    navigate(branding.showPosts ? '/dashboard/home' : '/dashboard/messages');
  };

  const handleNavigateToMessages = () => {
    handleNavClose();
    navigate('/dashboard/messages');
  };

  const handleNavigateToProfile = async () => {
    handleClose();
    if (!user) return;

    try {
      // Fetch the user's full profile data
      const profileData = await authService.getUserProfile(user.id);
      setDetailedUser(profileData);
      setProfileModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      // Fallback to using the user data we have
      setDetailedUser(user);
      setProfileModalOpen(true);
    }
  };

  const handleNavigateToSettings = () => {
    handleClose();
    handleNavClose();
    navigate('/dashboard/settings');
  };

  return (
    <header className="appbar appbar-sticky">
      <div className="toolbar">
        <div className="flex flex-align-center flex-grow-1">
          {/* Brand block — logo + name top-left on all screen sizes */}
          {branding.showBlockHeader && (
            <div
              className="flex flex-align-center"
              onClick={handleNavigateToHome}
              style={{ cursor: 'pointer', gap: '8px' }}
            >
              <img
                src={branding.logoUrl || '/logo192.png'}
                alt="Logo"
                style={{ height: 32 }}
              />
              <div style={{ fontSize: '20px', fontWeight: 'bold', fontFamily: 'inherit' }}>
                <span style={{ color: 'var(--primary-color, #3a9397)' }}>{branding.part1}</span>
                <span style={{ color: 'var(--primary-light, #ff9800)' }}>{branding.part2}</span>
              </div>
            </div>
          )}
        </div>

        {/* Mobile: avatar/hamburger toggle — top right */}
        <button
          className="icon-btn appbar-nav-mobile"
          onClick={handleNavMenu}
          style={{ color: 'var(--border-color, inherit)' }}
          aria-label="Menu"
        >
          {navMenuOpen ? (
            <span className="material-icons">close</span>
          ) : (user?.photoURL || user?.photo_url) ? (
            <div className="avatar" style={{ width: 32, height: 32, boxShadow: '0 0 0 2px rgba(255,255,255,0.7)', borderRadius: '50%' }}>
              <img src={user.photoURL || user.photo_url} alt="Menu" />
            </div>
          ) : (
            <div className="avatar" style={{ width: 32, height: 32, backgroundColor: avatarColors.backgroundColor, color: avatarColors.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, borderRadius: '50%', boxShadow: '0 0 0 2px rgba(255,255,255,0.7)' }}>
              {userInitial}
            </div>
          )}
        </button>

        {/* Desktop Navigation - Hidden on mobile */}
        <div className="appbar-nav-desktop">
          {branding.showPosts && (
            <button
              className="icon-btn mr-1"
              onClick={handleNavigateToHome}
              title="Home"
              style={{ color: 'var(--border-color, inherit)' }}
            >
              <span className="material-icons">home</span>
            </button>
          )}

          <button
            className="icon-btn mr-1"
            onClick={handleNavigateToDirectory}
            title="Family Directory"
            style={{ color: 'var(--border-color, inherit)' }}
          >
            <span className="material-icons">people</span>
          </button>

          {branding.showMap && (
            <button
              className="icon-btn mr-1"
              onClick={handleNavigateToMap}
              title="Family Map"
              style={{ color: 'var(--border-color, inherit)' }}
            >
              <span className="material-icons">map</span>
            </button>
          )}

          {branding.showBirthday && (
            <button
              className="icon-btn mr-1"
              onClick={handleNavigateToCalendar}
              title="Birthday Calendar"
              style={{ color: 'var(--border-color, inherit)' }}
            >
              <span className="material-icons">event</span>
            </button>
          )}

          <button
            className="icon-btn mr-1"
            onClick={handleNavigateToMessages}
            title="Messages"
            style={{ color: 'inherit', position: 'relative' }}
          >
            <span className="material-icons">chat_bubble</span>
            {totalUnreadMessages > 0 && (
              <span style={{
                position: 'absolute', top: '-4px', right: '-4px',
                backgroundColor: '#f44336', color: 'white', borderRadius: '50%',
                minWidth: '20px', height: '20px', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: '11px', fontWeight: 'bold',
                padding: '0 4px', border: '2px solid var(--primary-dark)'
              }}>
                {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
              </span>
            )}
          </button>

          {/* Notification bell */}
          <div ref={bellRef} style={{ position: 'relative' }}>
            <button
              className="icon-btn mr-1"
              onClick={() => setBellOpen(o => !o)}
              title="Notifications"
              style={{ color: 'inherit', position: 'relative' }}
            >
              <span className="material-icons">{bellOpen ? 'notifications' : totalNotifications > 0 ? 'notifications_active' : 'notifications_none'}</span>
              {totalNotifications > 0 && (
                <span style={{
                  position: 'absolute', top: '-4px', right: '-4px',
                  backgroundColor: 'var(--warning-color,#ff9800)', color: 'white', borderRadius: '50%',
                  minWidth: '18px', height: '18px', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '10px', fontWeight: 'bold', padding: '0 3px',
                }}>
                  {totalNotifications}
                </span>
              )}
            </button>

            {bellOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                background: 'var(--background-paper)', border: '1px solid var(--border-color)',
                borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                minWidth: 280, maxWidth: 340, zIndex: 2000, overflow: 'hidden',
              }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color)', fontWeight: 600, fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Notifications</span>
                  {loginAlerts.length > 0 && (
                    <button onClick={() => setLoginAlerts([])} style={{ border: 'none', background: 'transparent', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer', padding: '2px 6px' }}>
                      Clear logins
                    </button>
                  )}
                </div>

                {/* Real-time login alerts — admin only */}
                {loginAlerts.map(n => (
                  <div
                    key={n.id}
                    onClick={() => { setBellOpen(false); if (n.action) navigate(n.action); }}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 16px',
                      cursor: 'pointer', borderBottom: '1px solid var(--border-color)',
                      background: 'rgba(33,150,243,0.06)', transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(33,150,243,0.12)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'rgba(33,150,243,0.06)')}
                  >
                    <span className="material-icons" style={{ fontSize: 18, marginTop: 2, color: 'var(--info-color,#2196f3)' }}>login</span>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--text-primary)' }}>{n.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{n.body}</div>
                    </div>
                  </div>
                ))}

                {notifications.length === 0 && loginAlerts.length === 0 ? (
                  <div style={{ padding: '20px 16px', color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center' }}>
                    <span className="material-icons" style={{ fontSize: 32, display: 'block', marginBottom: 8, opacity: 0.4 }}>notifications_none</span>
                    All caught up!
                  </div>
                ) : notifications.map(n => (
                  <div
                    key={n.id}
                    onClick={() => { setBellOpen(false); if (n.action) navigate(n.action); }}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px',
                      cursor: n.action ? 'pointer' : 'default', borderBottom: '1px solid var(--border-color)',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span className="material-icons" style={{ fontSize: 20, marginTop: 2, color: n.type === 'approval' ? 'var(--warning-color,#ff9800)' : 'var(--primary-color)' }}>
                      {n.type === 'message' ? 'chat_bubble' : n.type === 'approval' ? 'how_to_reg' : 'info'}
                    </span>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--text-primary)' }}>{n.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{n.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {branding.showQR && (
            <button
              className="icon-btn mr-1"
              onClick={() => setQrModalOpen(true)}
              title="My QR Code"
              style={{ color: 'var(--border-color, inherit)' }}
            >
              <span className="material-icons">qr_code</span>
            </button>
          )}

          <button
            className="icon-btn mr-1"
            onClick={() => window.location.reload()}
            title="Refresh app"
            style={{ color: 'var(--border-color, inherit)' }}
          >
            <span className="material-icons">refresh</span>
          </button>

          <div style={{ marginRight: '8px' }}>
            <InstallButton />
          </div>

          <div className="position-relative">
            <button
              className="icon-btn"
              onClick={handleMenu}
              style={{ color: 'var(--border-color, inherit)' }}
              aria-label="account of current user"
            >
              {user?.photoURL || user?.photo_url ? (
                <div className="avatar" style={{ width: 32, height: 32, boxShadow: '0 0 0 2px rgba(255,255,255,0.7)', borderRadius: '50%' }}>
                  <img src={user?.photoURL || user?.photo_url} alt="Profile" />
                </div>
              ) : (
                <div className="avatar" style={{ width: 32, height: 32, backgroundColor: avatarColors.backgroundColor, color: avatarColors.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, borderRadius: '50%', boxShadow: '0 0 0 2px rgba(255,255,255,0.7)' }}>
                  {userInitial}
                </div>
              )}
            </button>

            {menuOpen && (
              <>
                <div
                  className="position-fixed"
                  style={{
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    zIndex: 1300,
                    backgroundColor: 'transparent'
                  }}
                  onClick={handleClose}
                ></div>
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  right: 0,
                  zIndex: 1350,
                  minWidth: '200px',
                  maxWidth: '280px',
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                  padding: '8px 0',
                  listStyle: 'none',
                  margin: '0',
                  overflow: 'auto'
                }}>
                  <div
                    onClick={handleNavigateToProfile}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderRadius: '0',
                      transition: 'background-color 0.15s',
                      minHeight: '48px',
                      textDecoration: 'none',
                      color: 'rgba(0, 0, 0, 0.87)',
                      whiteSpace: 'nowrap',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span className="material-icons mr-2" style={{ fontSize: '20px' }}>account_circle</span>
                    <span>Profile</span>
                  </div>

                  <div
                    onClick={handleNavigateToSettings}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderRadius: '0',
                      transition: 'background-color 0.15s',
                      minHeight: '48px',
                      textDecoration: 'none',
                      color: 'rgba(0, 0, 0, 0.87)',
                      whiteSpace: 'nowrap',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span className="material-icons mr-2" style={{ fontSize: '20px' }}>settings</span>
                    <span>Settings</span>
                  </div>

                  <div
                    onClick={handleToggleNotifications}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      cursor: notificationsBlocked ? 'default' : 'pointer',
                      borderRadius: '0',
                      transition: 'background-color 0.15s',
                      minHeight: '48px',
                      color: notificationsBlocked ? 'rgba(0,0,0,0.38)' : 'rgba(0, 0, 0, 0.87)',
                      whiteSpace: 'nowrap',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => { if (!notificationsBlocked) e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'; }}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    title={notificationsBlocked ? 'Blocked in browser settings' : undefined}
                  >
                    <span className="material-icons mr-2" style={{ fontSize: '20px' }}>
                      {notificationsBlocked ? 'notifications_off' : notificationsEnabled ? 'notifications_active' : 'notifications_off'}
                    </span>
                    <span>{notificationsBlocked ? 'Notifications blocked' : notificationsEnabled ? 'Notifications on' : 'Notifications off'}</span>
                  </div>

                  <div style={{ margin: '8px 0', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}></div>

                  <div
                    onClick={handleLogout}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderRadius: '0',
                      transition: 'background-color 0.15s',
                      color: '#d32f2f',
                      minHeight: '48px',
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span className="material-icons mr-2" style={{ fontSize: '20px' }}>logout</span>
                    <span>Logout</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mobile Nav Menu - Left Side Drawer */}
        {navMenuOpen && (
          <>
            <div
              className="position-fixed appbar-nav-mobile"
              style={{
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 1300,
                backgroundColor: 'rgba(0, 0, 0, 0.5)'
              }}
              onClick={handleNavClose}
            ></div>
            <div className="mobile-nav-drawer">
              {branding.showPosts && (
                <div
                  onClick={handleNavigateToHome}
                  className="mobile-menu-item"
                >
                  <span className="material-icons mr-2">home</span>
                  <span>Home</span>
                </div>
              )}

              <div
                onClick={handleNavigateToDirectory}
                className="mobile-menu-item"
              >
                <span className="material-icons mr-2">people</span>
                <span>Family Directory</span>
              </div>

              {branding.showMap && (
                <div
                  onClick={handleNavigateToMap}
                  className="mobile-menu-item"
                >
                  <span className="material-icons mr-2">map</span>
                  <span>Family Map</span>
                </div>
              )}

              {branding.showBirthday && (
                <div
                  onClick={handleNavigateToCalendar}
                  className="mobile-menu-item"
                >
                  <span className="material-icons mr-2">event</span>
                  <span>Birthday Calendar</span>
                </div>
              )}

              <div
                onClick={handleNavigateToMessages}
                className="mobile-menu-item"
                style={{ position: 'relative' }}
              >
                <span className="material-icons mr-2">chat_bubble</span>
                <span>Messages</span>
                {totalUnreadMessages > 0 && (
                  <span style={{
                    position: 'absolute',
                    right: '16px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    borderRadius: '12px',
                    minWidth: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    padding: '0 6px'
                  }}>
                    {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
                  </span>
                )}
              </div>

              <div style={{ margin: '8px 0', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}></div>

              <div
                onClick={() => window.location.reload()}
                className="mobile-menu-item"
              >
                <span className="material-icons mr-2">refresh</span>
                <span>Refresh App</span>
              </div>

              {branding.showQR && (
                <div
                  onClick={() => {
                    handleNavClose();
                    setQrModalOpen(true);
                  }}
                  className="mobile-menu-item"
                >
                  <span className="material-icons mr-2">qr_code</span>
                  <span>My QR Code</span>
                </div>
              )}

              <div
                onClick={handleNavigateToProfile}
                className="mobile-menu-item"
              >
                <span className="material-icons mr-2">account_circle</span>
                <span>My Profile</span>
              </div>

              {isAdminOrManager && (
                <div
                  onClick={handleNavigateToSettings}
                  className="mobile-menu-item"
                >
                  <span className="material-icons mr-2">settings</span>
                  <span>Settings</span>
                </div>
              )}

              <div
                onClick={handleToggleNotifications}
                className="mobile-menu-item"
                style={{ color: notificationsBlocked ? 'rgba(0,0,0,0.38)' : undefined }}
              >
                <span className="material-icons mr-2">
                  {notificationsBlocked || !notificationsEnabled ? 'notifications_off' : 'notifications_active'}
                </span>
                <span>
                  {notificationsBlocked ? 'Notifications blocked' : notificationsEnabled ? 'Notifications on' : 'Notifications off'}
                </span>
              </div>

              <div style={{ margin: '8px 0', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}></div>

              <div
                onClick={handleLogout}
                className="mobile-menu-item"
                style={{ color: '#d32f2f' }}
              >
                <span className="material-icons mr-2">logout</span>
                <span>Logout</span>
              </div>
            </div>
          </>
        )}
      </div>

      <ProfileModal
        open={profileModalOpen}
        onClose={() => {
          setProfileModalOpen(false);
          setDetailedUser(null);
        }}
        user={detailedUser}
        currentUser={user}
        onEdit={() => {}}
        onContact={() => {}}
        onViewLocation={() => {}}
        onUpdate={(updatedUser) => setDetailedUser(updatedUser)}
      />

      <QRCodeModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
      />
    </header>
  );
};